'use client'

import React, { useEffect, useState, Suspense } from "react";
import axios from "axios";
import { useRouter, useParams } from "next/navigation";
import DateCard from "@/components/DateCard/DateCard";
import FilterButton from "@/components/FilterButton";
import DateCardSkeleton from "@/components/skeletons/DateCardSkeleton";

import ReactGA from "react-ga4";
import Footer from "@/components/Footer/Footer";
import Header from "@/components/Header/Header";


function AppContent() {
  const router = useRouter();
  const params = useParams();
  
  const [dates, setDates] = useState([]);
  const [filteredDates, setFilteredDates] = useState([]);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [upcomingOnly, setUpcomingOnly] = useState(false);

  const currentYear = new Date().getFullYear();
  const yearFromParams = params.year;
  const year = yearFromParams && ['2025', '2026'].includes(yearFromParams) ? parseInt(yearFromParams) : 2025;

  const [isLoading, setIsLoading] = useState(true);

  const filterDates = () => {
    let filtered = [...dates];

    if (currentFilter !== "all") {
      filtered = filtered.filter(
        (date) => date.irrenunciable === currentFilter
      );
    }

    if (upcomingOnly) {
      const now = new Date();
      filtered = filtered.filter((date) => new Date(date.fecha) > now);
    }

    setFilteredDates(filtered);
  };

  ReactGA.initialize("G-WX180M3FC2");

  useEffect(() => {
    if (!yearFromParams || !['2025', '2026'].includes(yearFromParams)) {
      router.replace('/2025');
      return;
    }
    
    setIsLoading(true);
    axios.get(`/api/feriados?year=${year}`)
    .then(response => {
      setDates(response.data);
      setIsLoading(false);
    })
    .catch(error => {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    });

    ReactGA.send({ hitType: "pageview" });
  }, [year, yearFromParams, router]);
  
  useEffect(() => {
    filterDates();
  }, [dates, currentFilter, upcomingOnly]);

  const findNextHoliday = (dates) => {
    const now = new Date();
    const upcomingHolidays = dates
      .map((d) => new Date(d.fecha))
      .filter((date) => date > now);

    if (upcomingHolidays.length === 0) return null;

    return new Date(Math.min.apply(null, upcomingHolidays));
  };

  const [timeUntilNextHoliday, setTimeUntilNextHoliday] = useState(null);

  useEffect(() => {
    const nextHoliday = findNextHoliday(dates);

    const updateCounter = () => {
      const now = new Date();
      const timeDifference = nextHoliday - now;

      const totalSeconds = Math.floor(timeDifference / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeUntilNextHoliday({ days, hours, minutes, seconds });
    };

    if (nextHoliday) {
      updateCounter();
      const interval = setInterval(updateCounter, 1000);

      return () => clearInterval(interval);
    }
  }, [dates]);

  const handleFilterButtonClick = (label) => {
    ReactGA.event({
      category: "click events",
      action: "filter click",
      label: label,
    });

    if (label === "Todos") {
      setCurrentFilter("all");
    } else if (label === "Irrenunciable") {
      setCurrentFilter("1");
    } else if (label === "No Irrenunciable") {
      setCurrentFilter("0");
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col align-start items-center w-full">
      <Header year={year} />
      <div className="bg-red-700 p-4 w-full">
        <div className="text-sm font-semibold text-center">
          {isLoading || !timeUntilNextHoliday ? (
            <div className="animate-pulse">
              <div className="h-4 bg-red-500 rounded md:w-1/4 w-2/3 mx-auto"></div>
            </div>
          ) : (
            <div className="text-white">
              <span>Faltan: </span>
              <span>
                <b>{timeUntilNextHoliday.days}</b> días,{" "}
              </span>
              <span>
                <b>{timeUntilNextHoliday.hours}</b> horas,{" "}
              </span>
              <span>
                <b>{timeUntilNextHoliday.minutes}</b> minutos,{" "}
              </span>
              <span>
                <b>{timeUntilNextHoliday.seconds}</b> segundos para el próximo
                feriado.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto mt-2 p-4 flex flex-col md:flex-row justify-center items-center md:space-x-4 w-full">
        <div className="flex items-center mb-3 md:mb-0 md:mr-4">
          <label className="text-sm md:text-md font-semibold text-gray-600 mr-2">
            Año:
          </label>
          <select
            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-2 py-2"
            value={year}
            onChange={(e) => {
              const newYear = Number(e.target.value);
              router.push(`/${newYear}`);
            }}
          >
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
          </select>
        </div>
        <div className="flex flex-col md:flex-row items-center w-full md:w-auto">
          <p className="text-sm md:text-md font-semibold text-gray-600 mb-2 md:mb-0 md:mr-2">
            Filtrar por:
          </p>
          <div className="flex flex-wrap justify-center gap-2">
          <FilterButton
            isActive={currentFilter === "all"}
            onClick={() => handleFilterButtonClick("Todos")}
            label="Todos"
          />
          <FilterButton
            isActive={currentFilter === "1"}
            onClick={() => handleFilterButtonClick("Irrenunciable")}
            label="Irrenunciable"
          />
          <FilterButton
            isActive={currentFilter === "0"}
            onClick={() => handleFilterButtonClick("No Irrenunciable")}
            label="No Irrenunciable"
          />
          <FilterButton
            isActive={upcomingOnly}
            onClick={() => {
              handleFilterButtonClick(
                !upcomingOnly ? "Ocultar pasados" : "Ver Todos"
              );
              setUpcomingOnly(!upcomingOnly);
            }}
            label={!upcomingOnly ? "Ocultar pasados" : "Ver Todos"}
          />
        </div>
        </div>
      </div>

      <div className="container mx-auto p-4 md:w-1/2 sm:w-full">
        {isLoading ? (
          [...Array(8)].map((_, index) => (
            <DateCardSkeleton key={index} />
          ))
        ) : (
          filteredDates.map((date, index) => (
            <DateCard date={date} key={index} />
          ))
        )}
      </div>
      <Footer currentYear={currentYear} />
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={
      <div className="bg-gray-100 min-h-screen flex flex-col align-start items-center w-full">
        <div className="container mx-auto p-4 md:w-1/2 sm:w-full">
          {[...Array(8)].map((_, index) => (
            <DateCardSkeleton key={index} />
          ))}
        </div>
      </div>
    }>
      <AppContent />
    </Suspense>
  );
}

export default App;
